/* pcx-pdp.js - pdp-factory PC-style library.
   Gallery, size row, tier ladder, delivery pills, accordion, add to cart.

   THE CART PATH IS A VERBATIM PORT OF library-immy/assets/sw-pdp.js.
   It is duplicated on purpose: each library has to be droppable on its own. If you fix a
   cart bug in one, apply it to the other in the same commit. scripts/verify.py compares
   the two and fails the build when they drift. */
(function () {
  "use strict";

  /* ---------- theme adapter ----------
     Selectors differ per theme. snippets/pcx-tokens.liquid publishes the host theme's
     selectors on window.PCX_THEME; each entry is a comma-separated fallback chain, so an
     unconfigured store still has a fair chance. */
  var T = window.PCX_THEME || {};
  function themeEl(key, fallback) {
    var sel = T[key] || fallback;
    if (!sel) return null;
    var list = sel.split(",");
    for (var i = 0; i < list.length; i++) {
      var el = document.querySelector(list[i].trim());
      if (el) return el;
    }
    return null;
  }
  function cartTrigger() {
    return themeEl("cartTrigger", '.cart-trigger, [href$="/cart"], #cart-icon-bubble');
  }
  function cartDrawer() { return themeEl("cartDrawer", "cart-drawer"); }
  function cartRefreshEvent() { return T.refreshEvent || "cart-drawer:refresh"; }

  /* Subscription app embeds (Appstle, Recharge, Seal, Kaching) wrap BOTH window.fetch and
     XMLHttpRequest and rewrite /cart/add payloads to match their own hidden widget state,
     which silently strips the selling plan this buybox picked. A fresh same-origin iframe
     has an untouched fetch, so the request goes out exactly as built.
     NEVER "simplify" this away. It is the difference between the price on the button and
     the price on the invoice. */
  var _cleanFetch = null;
  function cleanFetch() {
    if (_cleanFetch) return _cleanFetch;
    try {
      var f = document.createElement("iframe");
      f.setAttribute("aria-hidden", "true");
      f.style.cssText = "position:absolute;width:0;height:0;border:0;opacity:0;pointer-events:none;";
      document.body.appendChild(f);
      var w = f.contentWindow;
      if (w && typeof w.fetch === "function" && w.fetch.toString().indexOf("[native code]") > -1) {
        _cleanFetch = w.fetch.bind(w);
        return _cleanFetch;
      }
    } catch (e) { /* fall through */ }
    _cleanFetch = window.fetch.bind(window);
    return _cleanFetch;
  }

  function money(cents, fmt) {
    var v = (cents / 100).toFixed(2);
    return (fmt || "${{amount}}").replace(/\{\{\s*amount\s*\}\}/, v);
  }

  /* ---------- gallery ---------- */
  function gallery(root) {
    var rail = root.querySelector("[data-pcx-rail]");
    if (!rail) return;
    var slides = Array.prototype.slice.call(rail.querySelectorAll("[data-pcx-slide]"));
    var dots = Array.prototype.slice.call(root.querySelectorAll("[data-pcx-dot]"));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-pcx-thumb]"));
    if (!slides.length) return;

    function mark(i) {
      dots.forEach(function (d, n) { d.classList.toggle("on", n === i); });
      thumbs.forEach(function (t, n) { t.classList.toggle("on", n === i); });
    }
    function go(i) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      rail.scrollTo({ left: slides[i].offsetLeft - rail.offsetLeft, behavior: "smooth" });
      mark(i);
    }
    dots.forEach(function (d, i) { d.addEventListener("click", function () { go(i); }); });
    thumbs.forEach(function (t, i) { t.addEventListener("click", function () { go(i); }); });

    /* Drag to scroll. Arrow buttons are ruled out and the thumbnail rail can be
       hidden, so a pointer drag is the only mouse affordance left. Touch is skipped
       so native momentum scrolling is untouched. scroll-snap is suspended mid-drag,
       otherwise mandatory snapping fights every scrollLeft we write. */
    (function () {
      var down = false, startX = 0, startLeft = 0;
      function nearest() {
        var mid = rail.scrollLeft + rail.clientWidth / 2, best = 0, bd = Infinity;
        slides.forEach(function (s, i) {
          var c = s.offsetLeft - rail.offsetLeft + s.clientWidth / 2, d = Math.abs(c - mid);
          if (d < bd) { bd = d; best = i; }
        });
        return best;
      }
      rail.addEventListener("pointerdown", function (e) {
        if (e.pointerType === "touch" || e.button !== 0) return;
        down = true; startX = e.clientX; startLeft = rail.scrollLeft;
        rail.style.scrollSnapType = "none";
        rail.classList.add("is-dragging");
      });
      rail.addEventListener("pointermove", function (e) {
        if (!down) return;
        e.preventDefault();
        rail.scrollLeft = startLeft - (e.clientX - startX);
      });
      function end() {
        if (!down) return;
        down = false;
        rail.classList.remove("is-dragging");
        rail.style.scrollSnapType = "";
        go(nearest());
      }
      rail.addEventListener("pointerup", end);
      rail.addEventListener("pointercancel", end);
      rail.addEventListener("pointerleave", end);
      rail.addEventListener("dragstart", function (e) { e.preventDefault(); });
    })();

    var tick;
    rail.addEventListener("scroll", function () {
      clearTimeout(tick);
      tick = setTimeout(function () {
        var mid = rail.scrollLeft + rail.clientWidth / 2, best = 0, bd = Infinity;
        slides.forEach(function (s, i) {
          var c = s.offsetLeft - rail.offsetLeft + s.clientWidth / 2, d = Math.abs(c - mid);
          if (d < bd) { bd = d; best = i; }
        });
        mark(best);
      }, 80);
    }, { passive: true });
    mark(0);

    /* variant switch repoints the gallery at that variant's image */
    root.addEventListener("pcx:variant", function (e) {
      var idx = e.detail && e.detail.imageIndex;
      if (typeof idx === "number" && idx >= 0) go(idx);
    });
  }

  /* ---------- selection state ----------
     root.pcxSel is the single source of truth for what the ATC will send.
     { variantId, sellingPlanId, quantity }. Every control writes to it and then calls
     render(); nothing reads a price off the DOM. */
  function selection(root) {
    var data = null;
    try { data = JSON.parse(root.querySelector("[data-pcx-data]").textContent); }
    catch (e) { return null; }

    var fmt = data.moneyFormat || "${{amount}}";
    root.pcxSel = { variantId: null, sellingPlanId: null, quantity: 1 };

    var sizeBtns = Array.prototype.slice.call(root.querySelectorAll("[data-pcx-size]"));
    var tierBtns = Array.prototype.slice.call(root.querySelectorAll("[data-pcx-tier]"));
    var planBtns = Array.prototype.slice.call(root.querySelectorAll("[data-pcx-plan]"));

    function variant() {
      var id = root.pcxSel.variantId;
      for (var i = 0; i < data.variants.length; i++) {
        if (String(data.variants[i].id) === String(id)) return data.variants[i];
      }
      return data.variants[0] || null;
    }

    /* unit price in cents for the current variant + plan, taken from the real
       selling_plan_allocations, never from a hardcoded ladder */
    function unitPrice() {
      var v = variant();
      if (!v) return 0;
      var plan = root.pcxSel.sellingPlanId;
      if (plan && v.allocations) {
        for (var i = 0; i < v.allocations.length; i++) {
          if (String(v.allocations[i].plan) === String(plan)) return v.allocations[i].price;
        }
      }
      return v.price;
    }

    function render() {
      var v = variant();
      if (!v) return;
      var qty = root.pcxSel.quantity || 1;
      var unit = unitPrice();
      var total = (root.pcxSel.tierPrice != null) ? root.pcxSel.tierPrice : unit * qty;
      var listTotal = (root.pcxSel.tierCompare != null)
        ? root.pcxSel.tierCompare
        : (v.compare && v.compare > v.price ? v.compare : v.price) * qty;

      /* Never render a saving off a compare_at that sits BELOW price: a mispriced catalog
         row otherwise shows a negative discount. Flag it, do not paper over it. */
      var save = listTotal > total ? listTotal - total : 0;

      set(root, "[data-pcx-price]", money(total, fmt));
      var wasEl = root.querySelector("[data-pcx-was]");
      if (wasEl) {
        wasEl.textContent = save ? money(listTotal, fmt) : "";
        wasEl.style.display = save ? "" : "none";
      }
      var saveEl = root.querySelector("[data-pcx-save]");
      if (saveEl) {
        var pct = save ? Math.round((save / listTotal) * 100) : 0;
        saveEl.textContent = pct ? "SAVE " + pct + "%" : "";
        saveEl.style.display = pct ? "" : "none";
      }
      var perEl = root.querySelector("[data-pcx-per]");
      if (perEl) {
        var servings = Number(v.servings || 0) * qty;
        perEl.textContent = servings ? money(Math.round(total / servings), fmt) + " per serving" : "";
        perEl.style.display = servings ? "" : "none";
      }

      var btn = root.querySelector("[data-pcx-atc]");
      if (btn) {
        var out = !v.available;
        btn.setAttribute("aria-disabled", out ? "true" : "false");
        var lbl = btn.querySelector("[data-pcx-atc-label]");
        if (lbl && out) lbl.textContent = data.soldOutLabel || "Sold out";
      }

      root.dispatchEvent(new CustomEvent("pcx:variant", {
        detail: { variant: v, imageIndex: v.imageIndex, unit: unit, total: total }
      }));
    }
    root.pcxRender = render;

    function pick(list, attr, apply) {
      list.forEach(function (b) {
        b.addEventListener("click", function () {
          if (b.hasAttribute("data-pcx-locked")) return;
          list.forEach(function (o) { o.classList.toggle("on", o === b); o.setAttribute("aria-checked", o === b ? "true" : "false"); });
          apply(b.getAttribute(attr), b);
          render();
        });
      });
      var first = list.filter(function (b) { return b.classList.contains("on"); })[0] || list[0];
      if (first && !first.hasAttribute("data-pcx-locked")) {
        first.classList.add("on");
        apply(first.getAttribute(attr), first);
      }
    }

    pick(sizeBtns, "data-pcx-size", function (val) { root.pcxSel.variantId = val; });
    pick(tierBtns, "data-pcx-tier", function (val, btn) {
      root.pcxSel.quantity = Number(val) || 1;
      /* A tier may carry an explicit charged total, for the case where a real automatic
         discount means unit*qty is NOT what checkout will take. Blank means old maths. */
      var tp = btn && btn.getAttribute("data-pcx-tier-price");
      var tc = btn && btn.getAttribute("data-pcx-tier-compare");
      root.pcxSel.tierPrice   = tp ? Math.round(parseFloat(tp) * 100) : null;
      root.pcxSel.tierCompare = tc ? Math.round(parseFloat(tc) * 100) : null;
    });
    pick(planBtns, "data-pcx-plan", function (val) {
      root.pcxSel.sellingPlanId = val && val !== "onetime" ? val : null;
    });

    if (!root.pcxSel.variantId && data.variants[0]) root.pcxSel.variantId = data.variants[0].id;
    render();
    return data;
  }

  function set(root, sel, text) {
    var el = root.querySelector(sel);
    if (el) el.textContent = text;
  }

  /* ---------- add to cart ---------- */
  function atc(root) {
    var btn = root.querySelector("[data-pcx-atc]");
    var err = root.querySelector("[data-pcx-err]");
    if (!btn) return;

    btn.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (btn.getAttribute("aria-disabled") === "true") return;
      var sel = root.pcxSel;
      if (!sel || !sel.variantId) return;

      btn.setAttribute("aria-disabled", "true");
      if (err) err.classList.remove("is-on");

      var item = { id: Number(sel.variantId), quantity: sel.quantity || 1 };
      if (sel.sellingPlanId) item.selling_plan = Number(sel.sellingPlanId);

      cleanFetch()((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : "/cart/add") + ".js", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ items: [item] })
      })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          btn.removeAttribute("aria-disabled");
          if (json.status && json.status !== 200) {
            if (err) {
              err.textContent = json.description || json.message || "Could not add to cart.";
              err.classList.add("is-on");
            }
            return;
          }
          document.dispatchEvent(new CustomEvent("pcx:cart-added", { bubbles: true }));
          var drawer = cartDrawer();
          var trigger = cartTrigger();
          var opener = typeof window.openCart === "function" ? window.openCart : null;
          var isDrawer = !!opener || (!!drawer && drawer.dataset.cartType !== "page");
          if (isDrawer && (opener || trigger)) {
            document.dispatchEvent(new CustomEvent(cartRefreshEvent(), { bubbles: true }));
            setTimeout(function () {
              if (drawer && drawer.hasAttribute("open")) return;
              if (opener) { try { opener(); return; } catch (e2) {} }
              if (trigger) trigger.click();
            }, 260);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || "/cart";
          }
        })
        .catch(function () {
          btn.removeAttribute("aria-disabled");
          window.location.href = (window.routes && window.routes.cart_url) || "/cart";
        });
    });
  }

  /* ---------- accordion ---------- */
  function accordion(root) {
    Array.prototype.slice.call(root.querySelectorAll("[data-pcx-acc-item]")).forEach(function (item) {
      var btn = item.querySelector("[data-pcx-acc-btn]");
      var panel = item.querySelector("[data-pcx-acc-panel]");
      if (!btn || !panel) return;
      btn.addEventListener("click", function () {
        var open = item.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
        panel.style.maxHeight = open ? panel.scrollHeight + "px" : "";
      });
    });
  }

  /* ---------- boot ---------- */
  function boot(root) {
    if (!root || root.dataset.pcxBooted) return;
    root.dataset.pcxBooted = "1";
    gallery(root);
    selection(root);
    atc(root);
    accordion(root);
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll("[data-pcx-buybox]")).forEach(boot);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else { init(); }
  /* theme editor re-renders the section without a page load */
  document.addEventListener("shopify:section:load", init);
})();
